/*
 * ROTAS69 cart-drawer upsell.
 * Populates the [data-r69-upsell] shell (rendered inside the cart drawer) with Shopify
 * Product Recommendations for the FIRST cart product: intent=complementary first, then
 * related if none. Items already in the cart are excluded. Adding a card uses /cart/add.js
 * with the `sections` param and dispatches the theme's own `shopify:cart:lines-update`
 * event so the drawer morphs, the count updates and the toast fires.
 *
 * No flicker / no reload on qty changes:
 *  - The shell is frozen against the cart morph (data-skip-node-update +
 *    data-skip-subtree-update in the snippet), so the injected cards are never wiped.
 *  - Recommendations are cached per source-product (memory + sessionStorage), so a cart
 *    change never refetches. Cart state is read from /cart.js (the frozen attribute is stale
 *    by design). We only rewrite the DOM when the visible product set actually changes.
 *
 * Desktop scroll: the card row scrolls horizontally; on hover/fine-pointer devices a pair of
 * prev/next arrows appears in the heading (touch keeps native swipe).
 */
// Скрипт — модуль: Shopify при отдаче превращает import() в обычном скрипте в require(), которого в браузере нет.
import { formatMoney } from '@theme/money-formatting';

(function () {
  if (window.__r69UpsellInit) return;
  window.__r69UpsellInit = true;

  var POOL = 10; // recommendations to request per cart product
  var SHOW = 20; // max cards to display (merged across all cart products)
  var root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  var currency = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'EUR';
  var LOCALE = (window.Shopify && Shopify.locale) || 'x';

  var recCache = {}; // "locale:sourceProductId" -> [products]
  var refreshTimer = null;

  // A page can hold more than one shell (the cart drawer + the /cart page); populate them all.
  // Per-shell "already painted" state lives on el.__lastIds so they don't clobber each other.
  function shells() {
    return Array.prototype.slice.call(document.querySelectorAll('[data-r69-upsell]'));
  }

  function money(cents, format) {
    if (format) return formatMoney(cents, format, currency);
    try {
      return (cents / 100).toLocaleString(undefined, { style: 'currency', currency: currency });
    } catch (e) {
      return (cents / 100).toFixed(2);
    }
  }

  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function firstAvailableVariant(p) {
    if (!p.variants || !p.variants.length) return null;
    for (var i = 0; i < p.variants.length; i++) {
      if (p.variants[i].available) return p.variants[i].id;
    }
    return null;
  }

  function fetchRecs(productId, intent) {
    var url =
      root + 'recommendations/products.json?product_id=' +
      encodeURIComponent(productId) + '&intent=' + intent + '&limit=' + POOL;
    return fetch(url, { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : { products: [] }; })
      .then(function (d) { return (d && d.products) || []; });
  }

  // Cache key is locale-scoped: titles/prices are localized, so lv and ru must not share a cache.
  function cacheKey(sourceId) {
    return 'r69ups:' + LOCALE + ':' + sourceId;
  }

  // Recommendations for a source product, cached in memory + sessionStorage for the session.
  function recsFor(sourceId) {
    var key = cacheKey(sourceId);
    if (recCache[key]) return Promise.resolve(recCache[key]);
    try {
      var stored = sessionStorage.getItem(key);
      if (stored) {
        var arr = JSON.parse(stored);
        recCache[key] = arr;
        return Promise.resolve(arr);
      }
    } catch (e) {}
    // Fetch complementary + related in PARALLEL (most products have no complementary, so the old
    // sequential "complementary then related" fallback doubled the latency); prefer complementary.
    return Promise.all([
      fetchRecs(sourceId, 'complementary'),
      fetchRecs(sourceId, 'related'),
    ]).then(function (res) {
      var products = res[0].length ? res[0] : res[1];
      recCache[key] = products;
      try { sessionStorage.setItem(key, JSON.stringify(products)); } catch (e) {}
      return products;
    });
  }

  function cartState() {
    return fetch(root + 'cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (c) {
        return { count: c.item_count, ids: (c.items || []).map(function (i) { return String(i.product_id); }) };
      })
      .catch(function () { return null; });
  }

  function refresh() {
    var els = shells();
    if (!els.length) return;
    cartState().then(function (st) {
      if (!st || !st.count) { els.forEach(function (el) { el.hidden = true; }); return; }
      var inCart = {};
      st.ids.forEach(function (id) { inCart[id] = true; });
      // Recommendations for EVERY product in the cart (each cached), merged round-robin so the
      // row isn't dominated by one product, de-duped, excluding what's already in the cart, and
      // capped at SHOW. The same merged set is painted into every shell.
      Promise.all(st.ids.map(function (id) {
        return recsFor(id).catch(function () { return []; });
      })).then(function (lists) {
        var seen = {}, merged = [], maxLen = 0;
        lists.forEach(function (l) { if (l.length > maxLen) maxLen = l.length; });
        for (var round = 0; round < maxLen && merged.length < SHOW; round++) {
          for (var i = 0; i < lists.length && merged.length < SHOW; i++) {
            var p = lists[i][round];
            if (!p) continue;
            var pid = String(p.id);
            if (inCart[pid] || seen[pid]) continue;
            seen[pid] = true;
            merged.push(p);
          }
        }
        shells().forEach(function (el) { paint(el, merged); });
      });
    });
  }

  function debouncedRefresh() {
    if (refreshTimer) clearTimeout(refreshTimer);
    refreshTimer = setTimeout(refresh, 90);
  }

  function paint(el, products) {
    var wrap = el.querySelector('[data-r69-upsell-items]');
    if (!wrap) return;
    if (!products.length) { el.hidden = true; el.__lastIds = ''; return; }

    var ids = products.map(function (p) { return p.id; }).join(',');
    // Same visible set already on screen -> do nothing (this is what kills the flicker on qty +/-).
    if (ids === el.__lastIds && wrap.children.length) {
      el.hidden = false;
      updateNav(el);
      return;
    }
    el.__lastIds = ids;

    var addLabel = el.getAttribute('data-add-label') || 'Add';
    var moneyFormat = el.getAttribute('data-money-format');
    wrap.innerHTML = products.map(function (p) { return card(p, addLabel, moneyFormat); }).join('');
    el.hidden = false;

    wrap.querySelectorAll('[data-r69-upsell-add]').forEach(function (btn) {
      btn.addEventListener('click', onAdd);
    });

    wireNav(el);
    wireDrag(el);
    wrap.scrollLeft = 0;
    // Measure overflow after the cards lay out. rAF catches the drawer; a short timeout also
    // catches the /cart page, where the flex layout / fonts settle a beat after first paint and
    // there's no drawer-open event to re-trigger the measure.
    requestAnimationFrame(function () { updateNav(el); });
    setTimeout(function () { updateNav(el); }, 300);
  }

  var PLUS_ICON =
    '<svg aria-hidden="true" viewBox="0 0 16 16" width="16" height="16" fill="none">' +
    '<path d="M8 3.25v9.5M3.25 8h9.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>';

  function sized(url, width) {
    return url + (url.indexOf('?') === -1 ? '?' : '&') + 'width=' + width;
  }

  function card(p, addLabel, moneyFormat) {
    var variantId = firstAvailableVariant(p);
    var img = p.featured_image || (p.images && p.images[0]) || '';
    var media = img
      ? '<span class="r69-upsell__media"><img src="' + sized(img, 240) + '" srcset="' + sized(img, 120) + ' 120w, ' + sized(img, 240) + ' 240w, ' + sized(img, 360) + ' 360w" sizes="116px" alt="" loading="lazy" draggable="false" width="120" height="120"></span>'
      : '<span class="r69-upsell__media"></span>';
    var label = escapeHtml(addLabel);
    var add = variantId
      ? '<button type="button" class="r69-upsell__add" data-r69-upsell-add data-variant-id="' + variantId + '" aria-label="' + label + '" title="' + label + '">' + PLUS_ICON + '</button>'
      : '<button type="button" class="r69-upsell__add" disabled aria-label="' + label + '">' + PLUS_ICON + '</button>';
    return (
      '<div class="r69-upsell__card">' +
      // aria-hidden + tabindex -1: the image link duplicates the title link right under it — without
      // this it was a second tab stop with no accessible name (axe link-name, a11y audit 11.09.2026).
      '<a href="' + p.url + '" class="r69-upsell__media-link" draggable="false" aria-hidden="true" tabindex="-1">' + media + '</a>' +
      '<a href="' + p.url + '" class="r69-upsell__title" draggable="false">' + escapeHtml(p.title) + '</a>' +
      '<div class="r69-upsell__buy">' +
      '<span class="r69-upsell__price">' + money(p.price, moneyFormat) + '</span>' +
      add +
      '</div>' +
      '</div>'
    );
  }

  function onAdd(event) {
    var btn = event.currentTarget;
    var variantId = btn.getAttribute('data-variant-id');
    if (!variantId) return;
    btn.disabled = true;

    var el = btn.closest('[data-r69-upsell]');
    var sectionId = (el && el.getAttribute('data-section-id')) || 'cart-drawer-section';
    var body = {
      items: [{ id: Number(variantId), quantity: 1 }],
      sections: sectionId,
      sections_url: window.location.pathname,
    };

    var promise = fetch(root + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    })
      .then(function (r) {
        if (!r.ok) throw new Error('add failed');
        return r.json();
      })
      .then(function (data) {
        return { detail: { sections: data.sections } };
      });

    // Dispatch the theme's own cart event so cart-items-component morphs the drawer,
    // the header count updates and the toast fires.
    var ev = new Event('shopify:cart:lines-update');
    ev.action = 'add';
    ev.promise = promise;
    document.dispatchEvent(ev);

    promise.catch(function () { btn.disabled = false; });
  }

  // ---- Desktop nav arrows -------------------------------------------------
  function wireNav(el) {
    if (el.__navWired) return;
    el.__navWired = true;
    var wrap = el.querySelector('[data-r69-upsell-items]');
    var prev = el.querySelector('[data-r69-upsell-prev]');
    var next = el.querySelector('[data-r69-upsell-next]');
    if (!wrap) return;

    // Animate by hand: direct scrollLeft always works, while `behavior:'smooth'` is unreliable
    // (disabled under reduced-motion / some environments), which would make the arrows do nothing.
    function animateTo(target) {
      target = Math.max(0, Math.min(target, wrap.scrollWidth - wrap.clientWidth));
      var start = wrap.scrollLeft;
      var steps = 16, i = 0;
      (function tick() {
        i++;
        var p = i / steps;
        var ease = 1 - Math.pow(1 - p, 3);
        wrap.scrollLeft = start + (target - start) * ease;
        if (i < steps) requestAnimationFrame(tick);
      })();
    }
    function step(dir) {
      var firstCard = wrap.querySelector('.r69-upsell__card');
      var w = firstCard ? firstCard.getBoundingClientRect().width + 12 : 128;
      animateTo(wrap.scrollLeft + dir * w); // one card per click, every device
    }
    if (prev) prev.addEventListener('click', function () { step(-1); });
    if (next) next.addEventListener('click', function () { step(1); });
    wrap.addEventListener('scroll', function () { updateNav(el); }, { passive: true });
    window.addEventListener('resize', function () { updateNav(el); });
    // The row only gets its width once the drawer has opened/animated, so the initial measure can
    // run too early. Re-measure whenever the row resizes, so the arrows appear the moment it overflows.
    if (window.ResizeObserver) new ResizeObserver(function () { updateNav(el); }).observe(wrap);
  }

  function updateNav(el) {
    var wrap = el.querySelector('[data-r69-upsell-items]');
    var nav = el.querySelector('[data-r69-upsell-nav]');
    if (!wrap || !nav) return;
    var overflow = wrap.scrollWidth - wrap.clientWidth > 4;
    nav.hidden = !overflow;
    el.classList.toggle('r69-upsell--has-nav', overflow);
    var prev = el.querySelector('[data-r69-upsell-prev]');
    var next = el.querySelector('[data-r69-upsell-next]');
    var max = wrap.scrollWidth - wrap.clientWidth;
    if (prev) prev.disabled = wrap.scrollLeft <= 2;
    if (next) next.disabled = wrap.scrollLeft >= max - 2;
  }

  // ---- Desktop click-drag to scroll (mouse only; touch keeps native swipe) ----
  function wireDrag(el) {
    var wrap = el.querySelector('[data-r69-upsell-items]');
    if (!wrap || wrap.__dragWired) return;
    wrap.__dragWired = true;
    var down = false, moved = false, startX = 0, startScroll = 0;

    // Native image/link dragging steals the mouse gesture (ghost-drag) and stops our scroll from
    // ever engaging — kill it so a mouse press-and-drag scrolls the row instead.
    wrap.addEventListener('dragstart', function (e) { e.preventDefault(); });

    wrap.addEventListener('pointerdown', function (e) {
      if (e.pointerType !== 'mouse' || e.button !== 0) return;
      down = true; moved = false; startX = e.clientX; startScroll = wrap.scrollLeft;
    });
    wrap.addEventListener('pointermove', function (e) {
      if (!down || e.pointerType !== 'mouse') return;
      var dx = e.clientX - startX;
      if (!moved && Math.abs(dx) > 4) {
        moved = true;
        wrap.classList.add('r69-dragging');
        try { wrap.setPointerCapture(e.pointerId); } catch (_) {}
      }
      if (moved) wrap.scrollLeft = startScroll - dx;
    });
    function release() {
      if (!down) return;
      down = false;
      wrap.classList.remove('r69-dragging');
    }
    wrap.addEventListener('pointerup', release);
    wrap.addEventListener('pointercancel', release);
    // Swallow the click that fires right after a drag so it doesn't open a card / add to bag.
    wrap.addEventListener('click', function (e) {
      if (moved) { e.preventDefault(); e.stopPropagation(); moved = false; }
    }, true);
  }

  // ---- Wiring -------------------------------------------------------------
  document.addEventListener('shopify:cart:lines-update', function (e) {
    if (e && e.promise && typeof e.promise.then === 'function') {
      e.promise.then(function () { debouncedRefresh(); }).catch(function () {});
    } else {
      debouncedRefresh();
    }
  });
  document.addEventListener('theme-drawer:open', refresh);

  // Populate eagerly ONLY on the /cart page, where the shell is inline and there is no
  // drawer-open event to trigger it. Everywhere else (PDP, collections, home) the drawer upsell
  // is populated lazily on `theme-drawer:open` / cart add. Running the cart.js fetch + paint +
  // ResizeObserver at page load forced a style/layout recalc (~36ms, measured via the Long
  // Animation Frames API) on every page for a drawer the shopper may never open on that view.
  function isCartPage() {
    var m = document.getElementById('MainContent');
    return !!(m && /^cart/.test(m.getAttribute('data-template') || ''));
  }
  function refreshIfCartPage() { if (isCartPage()) refresh(); }
  if (document.readyState !== 'loading') refreshIfCartPage();
  else document.addEventListener('DOMContentLoaded', refreshIfCartPage);
})();
